/**
 * The site's language (a BCP 47 tag) and time zone (an IANA zone). Both are optional owner settings;
 * a site without them behaves exactly as it did before they existed.
 */
/** The canonical form of a language tag, or null when it isn't one. `en-sg` becomes `en-SG`. */
export function canonicalLanguage(tag) {
    const text = tag.trim();
    // Intl accepts a few shapes nobody means as a page language (private use, grandfathered, long extensions).
    if (!text || text.length > 35 || !/^[A-Za-z]{2,3}(?:-[A-Za-z0-9]{2,8}){0,3}$/.test(text))
        return null;
    try {
        const [canonical] = Intl.getCanonicalLocales(text);
        return canonical ?? null;
    }
    catch {
        return null;
    }
}
export function isValidLanguage(tag) { return canonicalLanguage(tag) !== null; }
/** Whether the runtime knows the IANA zone (`Asia/Singapore`, `UTC`). */
export function isValidTimeZone(zone) {
    // Intl also takes bare legacy names ("Singapore", "EST"); an owner-facing zone is an Area/City name, or UTC.
    if (!zone || zone.length > 64 || zone !== zone.trim() || !(zone === "UTC" || /^[A-Za-z_]+(?:\/[A-Za-z0-9_+-]+)+$/.test(zone)))
        return false;
    try {
        new Intl.DateTimeFormat("en", { timeZone: zone });
        return true;
    }
    catch {
        return false;
    }
}
/** `og:locale`, written language_TERRITORY (`en_SG`). A language alone gets its likeliest territory (`ms` becomes `ms_MY`). */
export function ogLocale(language) {
    const canonical = canonicalLanguage(language);
    if (!canonical)
        return language.replaceAll("-", "_");
    try {
        const locale = new Intl.Locale(canonical);
        const region = locale.region ?? locale.maximize().region;
        return region ? `${locale.language}_${region}` : locale.language;
    }
    catch {
        return canonical.replaceAll("-", "_");
    }
}
